import SEOHead from '../../components/seo/SEOHead';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BlogCard from '../../components/base/BlogCard';
import { blogPosts } from '../../mocks/blog-posts';

export default function CategoryReformaTributariaPage() {
  const categoryPosts = blogPosts.filter(post => 
    post.category === 'Reforma Tributária'
  );

  return (
    <>
      <SEOHead
        title="Reforma Tributária Brasil 2025 | Guia Completo IBS, CBS e IVA Dual"
        description="Tudo sobre a Reforma Tributária: entenda o IVA Dual, transição do IBS e CBS, impactos no seu negócio e como se preparar. Guia completo atualizado 2025."
        keywords="reforma tributária, IVA dual, IBS, CBS, impostos brasil, tributação consumo"
        canonical="https://ativonews.com.br/categoria/reforma-tributaria"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 pt-32 pb-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3ab0167a108b5f202a8e2a212105515d.jpg"
            alt="Escritório de Contabilidade Moderno"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/80 via-teal-900/70 to-cyan-900/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-government-line text-white text-xl"></i>
              <span className="text-white font-medium">Reforma Tributária</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Reforma Tributária<br />
              <span className="text-emerald-200">Guia Completo 2026</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Tudo o que você precisa saber sobre a maior transformação do sistema tributário brasileiro. 
              Prepare-se para o IVA Dual, CBS e IBS com nosso guia completo e atualizado.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                <div className="text-3xl font-bold text-white">2026</div>
                <div className="text-sm text-white/80">Início da Transição</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                <div className="text-3xl font-bold text-white">2033</div>
                <div className="text-sm text-white/80">Implementação Total</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
                <div className="text-3xl font-bold text-white">IVA Dual</div>
                <div className="text-sm text-white/80">CBS + IBS</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Fundamentos da Reforma Tributária */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-emerald-50 px-4 py-2 rounded-full mb-4">
              <i className="ri-question-answer-line text-emerald-600 text-xl"></i>
              <span className="text-emerald-700 font-semibold text-sm">Perguntas e Respostas</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fundamentos da Reforma Tributária
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entenda os principais conceitos e mudanças que impactarão sua empresa
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            {/* Card Principal - Alíquota Única */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12 border-2 border-blue-200 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-2xl flex-shrink-0">
                  <i className="ri-percent-line text-white text-4xl"></i>
                </div>
                <h3 className="text-4xl font-bold text-gray-900 leading-tight">
                  A "Alíquota Única" e Imposto por Fora
                </h3>
              </div>
              
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Diferente do sistema atual, o novo modelo adota o <strong>imposto por fora</strong>, 
                onde a alíquota incide sobre o valor da operação, não sobre si mesma. Esta mudança 
                traz mais transparência e facilita o cálculo tributário.
              </p>

              {/* Comparação Antes x Depois */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Sistema Atual */}
                <div className="bg-white rounded-2xl p-8 border-2 border-red-200">
                  <div className="flex items-center gap-3 mb-6">
                    <i className="ri-close-circle-fill text-red-600 text-2xl"></i>
                    <h4 className="text-2xl font-bold text-red-900">Sistema Atual (Por Dentro)</h4>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-red-50 rounded-xl p-6">
                      <p className="text-base text-gray-600 mb-3">Produto de R$ 100,00</p>
                      <p className="text-lg text-gray-800 mb-2">Alíquota: 20%</p>
                      <p className="text-lg text-gray-800 mb-2">Base de cálculo: R$ 100,00 ÷ 0,80 = <strong>R$ 125,00</strong></p>
                      <p className="text-lg text-gray-800 mb-2">Imposto: R$ 125,00 × 20% = <strong>R$ 25,00</strong></p>
                      <div className="border-t border-red-200 mt-4 pt-4">
                        <p className="text-2xl font-bold text-red-900">Total: R$ 125,00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-base text-gray-600">
                      <i className="ri-error-warning-line text-red-500 mt-1"></i>
                      <p>Cálculo complexo e pouco transparente para o consumidor</p>
                    </div>
                  </div>
                </div>

                {/* Novo Sistema */}
                <div className="bg-white rounded-2xl p-8 border-2 border-green-200">
                  <div className="flex items-center gap-3 mb-6">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <h4 className="text-2xl font-bold text-green-900">Novo Sistema (Por Fora)</h4>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-green-50 rounded-xl p-6">
                      <p className="text-base text-gray-600 mb-3">Produto de R$ 100,00</p>
                      <p className="text-lg text-gray-800 mb-2">Alíquota: 20%</p>
                      <p className="text-lg text-gray-800 mb-2">Base de cálculo: <strong>R$ 100,00</strong></p>
                      <p className="text-lg text-gray-800 mb-2">Imposto: R$ 100,00 × 20% = <strong>R$ 20,00</strong></p>
                      <div className="border-t border-green-200 mt-4 pt-4">
                        <p className="text-2xl font-bold text-green-900">Total: R$ 120,00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-base text-gray-600">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p>Cálculo simples e transparente, fácil de entender</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exemplos Práticos com Alíquota Padrão */}
              <div className="bg-white rounded-2xl p-8 border-2 border-cyan-200 mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-calculator-line text-cyan-600 text-2xl"></i>
                  Exemplos com a Alíquota Padrão (26,5%)
                </h4>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-cyan-200">
                        <th className="text-left py-4 px-6 font-semibold text-gray-900 text-lg">Produto/Serviço</th>
                        <th className="text-right py-4 px-6 font-semibold text-gray-900 text-lg">Valor Base</th>
                        <th className="text-right py-4 px-6 font-semibold text-gray-900 text-lg">Imposto (26,5%)</th>
                        <th className="text-right py-4 px-6 font-semibold text-cyan-900 text-lg">Valor Final</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-cyan-50 transition-colors">
                        <td className="py-4 px-6 text-gray-800 text-base">Notebook</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 3.000,00</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 795,00</td>
                        <td className="py-4 px-6 text-right font-bold text-cyan-900 text-lg">R$ 3.795,00</td>
                      </tr>
                      <tr className="hover:bg-cyan-50 transition-colors">
                        <td className="py-4 px-6 text-gray-800 text-base">Consultoria Contábil</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 1.500,00</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 397,50</td>
                        <td className="py-4 px-6 text-right font-bold text-cyan-900 text-lg">R$ 1.897,50</td>
                      </tr>
                      <tr className="hover:bg-cyan-50 transition-colors">
                        <td className="py-4 px-6 text-gray-800 text-base">Smartphone</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 2.500,00</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 662,50</td>
                        <td className="py-4 px-6 text-right font-bold text-cyan-900 text-lg">R$ 3.162,50</td>
                      </tr>
                      <tr className="hover:bg-cyan-50 transition-colors">
                        <td className="py-4 px-6 text-gray-800 text-base">Jantar em Restaurante</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 200,00</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 53,00</td>
                        <td className="py-4 px-6 text-right font-bold text-cyan-900 text-lg">R$ 253,00</td>
                      </tr>
                      <tr className="hover:bg-cyan-50 transition-colors">
                        <td className="py-4 px-6 text-gray-800 text-base">Curso Online</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 500,00</td>
                        <td className="py-4 px-6 text-right text-gray-800 text-base">R$ 132,50</td>
                        <td className="py-4 px-6 text-right font-bold text-cyan-900 text-lg">R$ 632,50</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Alíquotas Reduzidas */}
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 border-2 border-teal-200 mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-price-tag-3-line text-teal-600 text-2xl"></i>
                  Alíquotas Reduzidas (Setores Específicos)
                </h4>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 border-2 border-teal-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-heart-pulse-line text-teal-600 text-2xl"></i>
                      <h5 className="font-bold text-gray-900 text-xl">Saúde</h5>
                    </div>
                    <p className="text-4xl font-bold text-teal-600 mb-2">60%</p>
                    <p className="text-base text-gray-600">de redução</p>
                    <p className="text-sm text-gray-500 mt-3">Alíquota efetiva: ~10,6%</p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-teal-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-book-open-line text-teal-600 text-2xl"></i>
                      <h5 className="font-bold text-gray-900 text-xl">Educação</h5>
                    </div>
                    <p className="text-4xl font-bold text-teal-600 mb-2">60%</p>
                    <p className="text-base text-gray-600">de redução</p>
                    <p className="text-sm text-gray-500 mt-3">Alíquota efetiva: ~10,6%</p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border-2 border-teal-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-bus-line text-teal-600 text-2xl"></i>
                      <h5 className="font-bold text-gray-900 text-xl">Transporte</h5>
                    </div>
                    <p className="text-4xl font-bold text-teal-600 mb-2">40%</p>
                    <p className="text-base text-gray-600">de redução</p>
                    <p className="text-sm text-gray-500 mt-3">Alíquota efetiva: ~15,9%</p>
                  </div>
                </div>
              </div>

              {/* Vantagens do Novo Sistema */}
              <div className="bg-white rounded-2xl p-8 border-2 border-cyan-200 mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-lightbulb-line text-cyan-600 text-2xl"></i>
                  Vantagens do Imposto "Por Fora"
                </h4>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-xl flex-shrink-0">
                      <i className="ri-eye-line text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 text-lg">Transparência Total</h5>
                      <p className="text-base text-gray-600">O consumidor vê exatamente quanto está pagando de imposto</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-xl flex-shrink-0">
                      <i className="ri-calculator-line text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 text-lg">Cálculo Simplificado</h5>
                      <p className="text-base text-gray-600">Multiplicação direta, sem fórmulas complexas</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-xl flex-shrink-0">
                      <i className="ri-file-list-line text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 text-lg">Nota Fiscal Clara</h5>
                      <p className="text-base text-gray-600">Discriminação separada do valor do produto e do imposto</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-xl flex-shrink-0">
                      <i className="ri-global-line text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2 text-lg">Padrão Internacional</h5>
                      <p className="text-base text-gray-600">Alinhamento com práticas adotadas em mais de 170 países</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nota de Rodapé */}
              <div className="bg-cyan-100 rounded-xl p-6 border-l-4 border-cyan-600">
                <p className="text-base text-gray-700">
                  <strong>Importante:</strong> A alíquota padrão de 26,5% ainda pode ser ajustada pelo Congresso Nacional. 
                  O valor final dependerá das definições sobre regimes especiais e alíquotas reduzidas para cada setor.
                </p>
              </div>
            </div>
          </div>

          {/* Regime Geral e Fato Gerador */}
          <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-center">
              <i className="ri-file-list-3-line text-white text-5xl mb-3"></i>
              <h3 className="text-3xl font-bold text-white mb-2">
                Regime Geral e Fato Gerador
              </h3>
              <p className="text-lg text-white/90">
                Entenda como funciona a tributação no novo sistema
              </p>
            </div>

            <div className="p-8">
              {/* Conceitos Fundamentais */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-indigo-50 rounded-xl p-6 border-l-4 border-indigo-600">
                  <div className="flex items-start gap-3 mb-3">
                    <i className="ri-book-open-line text-indigo-600 text-2xl"></i>
                    <h4 className="text-xl font-bold text-gray-900">Regime Geral</h4>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    É o regime padrão de tributação que se aplica a todas as operações de consumo, 
                    salvo exceções específicas. Substitui os atuais ICMS, ISS, PIS, COFINS e IPI 
                    pelos novos CBS e IBS.
                  </p>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border-l-4 border-purple-600">
                  <div className="flex items-start gap-3 mb-3">
                    <i className="ri-flashlight-line text-purple-600 text-2xl"></i>
                    <h4 className="text-xl font-bold text-gray-900">Fato Gerador</h4>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    É o evento que dá origem à obrigação tributária. No novo sistema, o fato gerador 
                    ocorre no momento da entrega do bem ou da conclusão do serviço, não mais na 
                    emissão da nota fiscal.
                  </p>
                </div>
              </div>

              {/* Características do Regime Geral */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-indigo-600"></i>
                  Características do Regime Geral
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                      <i className="ri-refresh-line text-indigo-600 text-xl"></i>
                    </div>
                    <h5 className="font-bold text-gray-900 mb-2">Não Cumulatividade Plena</h5>
                    <p className="text-sm text-gray-600">
                      Crédito integral sobre todas as aquisições relacionadas à atividade econômica
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                      <i className="ri-map-pin-line text-purple-600 text-xl"></i>
                    </div>
                    <h5 className="font-bold text-gray-900 mb-2">Destino</h5>
                    <p className="text-sm text-gray-600">
                      Tributação no local de consumo, não mais na origem da produção
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
                      <i className="ri-percent-line text-indigo-600 text-xl"></i>
                    </div>
                    <h5 className="font-bold text-gray-900 mb-2">Alíquota Única</h5>
                    <p className="text-sm text-gray-600">
                      Mesma alíquota para todos os setores, com exceções específicas
                    </p>
                  </div>
                </div>
              </div>

              {/* Fato Gerador - Mudanças Importantes */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-time-line text-purple-600"></i>
                  Fato Gerador - Mudanças Importantes
                </h4>
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                        <i className="ri-close-circle-line text-red-600 text-2xl"></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-bold text-gray-900 mb-2">Sistema Atual (até 2026)</h5>
                      <p className="text-gray-700 mb-3">
                        Fato gerador ocorre na <strong>emissão da nota fiscal</strong>, mesmo que o produto 
                        não tenha sido entregue ou o serviço não tenha sido prestado.
                      </p>
                      <div className="bg-white rounded-lg p-3 text-sm text-gray-600">
                        <strong>Problema:</strong> Empresas pagam imposto antes de receber o pagamento do cliente
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                        <i className="ri-check-double-line text-emerald-600 text-2xl"></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-bold text-gray-900 mb-2">Novo Sistema (a partir de 2027)</h5>
                      <p className="text-gray-700 mb-3">
                        Fato gerador ocorre na <strong>entrega do bem ou conclusão do serviço</strong>, 
                        alinhando a tributação com a realidade econômica.
                      </p>
                      <div className="bg-white rounded-lg p-3 text-sm text-gray-600">
                        <strong>Benefício:</strong> Melhor fluxo de caixa e tributação mais justa
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exemplos Práticos */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-amber-500"></i>
                  Exemplos Práticos
                </h4>

                {/* Exemplo 1 - Comércio */}
                <div className="bg-blue-50 rounded-xl p-6 mb-4 border-l-4 border-blue-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <h5 className="text-xl font-bold text-gray-900">Comércio de Eletrônicos</h5>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">📦 Situação:</p>
                      <p className="text-gray-600">
                        Loja vende notebook por R$ 5.000 em São Paulo para cliente no Rio de Janeiro. 
                        Nota fiscal emitida dia 10/01, produto entregue dia 15/01.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-red-100 rounded-lg p-4">
                        <p className="text-sm font-bold text-red-800 mb-2">❌ Sistema Atual:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• Fato gerador: 10/01 (emissão NF)</li>
                          <li>• ICMS devido: São Paulo</li>
                          <li>• Imposto pago antes da entrega</li>
                        </ul>
                      </div>

                      <div className="bg-emerald-100 rounded-lg p-4">
                        <p className="text-sm font-bold text-emerald-800 mb-2">✅ Novo Sistema:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• Fato gerador: 15/01 (entrega)</li>
                          <li>• IBS devido: Rio de Janeiro</li>
                          <li>• Tributação no destino</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-indigo-100 rounded-lg p-4">
                      <p className="text-sm font-bold text-indigo-800 mb-2">💡 Impacto:</p>
                      <p className="text-sm text-gray-700">
                        A loja tem 5 dias a mais de fluxo de caixa e o imposto fica no estado de consumo (RJ), 
                        beneficiando a economia local.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Exemplo 2 - Serviços */}
                <div className="bg-purple-50 rounded-xl p-6 mb-4 border-l-4 border-purple-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <h5 className="text-xl font-bold text-gray-900">Prestação de Serviços</h5>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">🛠️ Situação:</p>
                      <p className="text-gray-600">
                        Consultoria contábil presta serviço de R$ 10.000. Contrato assinado dia 01/02, 
                        serviço executado entre 01/02 e 28/02, relatório final entregue dia 28/02.
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-red-100 rounded-lg p-4">
                        <p className="text-sm font-bold text-red-800 mb-2">❌ Sistema Atual:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• Fato gerador: emissão da NF</li>
                          <li>• ISS devido imediatamente</li>
                          <li>• Pode ser antes do pagamento</li>
                        </ul>
                      </div>

                      <div className="bg-emerald-100 rounded-lg p-4">
                        <p className="text-sm font-bold text-emerald-800 mb-2">✅ Novo Sistema:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• Fato gerador: 28/02 (conclusão)</li>
                          <li>• CBS devido após entrega</li>
                          <li>• Alinhado com a prestação real</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-purple-100 rounded-lg p-4">
                      <p className="text-sm font-bold text-purple-800 mb-2">💡 Impacto:</p>
                      <p className="text-sm text-gray-700">
                        A empresa só paga o imposto após concluir todo o serviço, melhorando o fluxo de caixa 
                        e evitando tributação sobre serviços não concluídos.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Exemplo 3 - Indústria */}
                <div className="bg-orange-50 rounded-xl p-6 border-l-4 border-orange-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <h5 className="text-xl font-bold text-gray-900">Indústria com Cadeia Produtiva</h5>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">🏭 Situação:</p>
                      <p className="text-gray-600">
                        Fábrica de móveis compra madeira (R$ 20.000 + R$ 3.400 de impostos) e vende 
                        móveis por R$ 50.000 para loja varejista.
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm font-bold text-gray-800 mb-3">📊 Cálculo no Regime Geral:</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-gray-700">Venda de móveis:</span>
                          <span className="font-semibold">R$ 50.000</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <span className="text-gray-700">CBS + IBS (26,5%):</span>
                          <span className="font-semibold text-blue-700">R$ 13.250</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-emerald-50 rounded">
                          <span className="text-gray-700">(-) Crédito da madeira:</span>
                          <span className="font-semibold text-emerald-700">-R$ 3.400</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-indigo-100 rounded border-t-2 border-indigo-600">
                          <span className="text-gray-900 font-bold">Imposto a pagar:</span>
                          <span className="font-bold text-indigo-700 text-lg">R$ 9.850</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-100 rounded-lg p-4">
                      <p className="text-sm font-bold text-orange-800 mb-2">💡 Impacto:</p>
                      <p className="text-sm text-gray-700">
                        <strong>Não cumulatividade plena:</strong> A fábrica recupera 100% do imposto pago 
                        na compra da madeira, evitando efeito cascata. O fato gerador ocorre apenas quando 
                        os móveis são entregues à loja.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quadro Comparativo Final */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  📋 Resumo Comparativo
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left p-3 font-bold text-gray-900">Aspecto</th>
                        <th className="text-left p-3 font-bold text-red-700">Sistema Atual</th>
                        <th className="text-left p-3 font-bold text-emerald-700">Novo Sistema</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-white">
                        <td className="p-3 font-semibold text-gray-700">Fato Gerador</td>
                        <td className="p-3 text-gray-600">Emissão da nota fiscal</td>
                        <td className="p-3 text-gray-600">Entrega/conclusão</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="p-3 font-semibold text-gray-700">Local de Tributação</td>
                        <td className="p-3 text-gray-600">Origem (produtor)</td>
                        <td className="p-3 text-gray-600">Destino (consumidor)</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-3 font-semibold text-gray-700">Créditos</td>
                        <td className="p-3 text-gray-600">Limitados e complexos</td>
                        <td className="p-3 text-gray-600">Plenos e automáticos</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="p-3 font-semibold text-gray-700">Alíquotas</td>
                        <td className="p-3 text-gray-600">Múltiplas e variadas</td>
                        <td className="p-3 text-gray-600">Única (26,5%)</td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-3 font-semibold text-gray-700">Fluxo de Caixa</td>
                        <td className="p-3 text-gray-600">Impacto negativo</td>
                        <td className="p-3 text-gray-600">Melhor gestão</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Atenção Especial */}
              <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line text-amber-600 text-2xl flex-shrink-0"></i>
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">⚠️ Atenção para o Período de Transição</h5>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Entre 2027 e 2032, haverá convivência entre os sistemas antigo e novo. As empresas 
                      precisarão gerenciar dois regimes simultaneamente, com fatos geradores diferentes. 
                      Planejamento e sistemas adequados serão essenciais para evitar erros e otimizar a carga tributária.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infográfico Antes x Depois */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full mb-4">
              <i className="ri-exchange-line text-amber-600 text-xl"></i>
              <span className="text-amber-700 font-semibold text-sm">Comparativo Visual</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Antes x Depois da Reforma
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Visualize as principais mudanças do sistema tributário brasileiro
            </p>
          </div>

          {/* Infográfico Principal */}
          <div className="relative">
            {/* Seta Central */}
            <div className="hidden lg:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
              <div className="bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full p-6 shadow-2xl">
                <i className="ri-arrow-right-line text-white text-4xl"></i>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
              {/* ANTES */}
              <div className="relative">
                <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 border-2 border-red-200 shadow-xl h-full">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-red-500 rounded-2xl">
                      <i className="ri-close-circle-line text-white text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-red-900">ANTES</h3>
                      <p className="text-red-600 font-medium">Sistema Atual</p>
                    </div>
                  </div>

                  {/* Impostos Atuais */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="ri-stack-line text-red-500"></i>
                      5 Tributos Diferentes
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-xl p-4 border border-red-200 text-center">
                        <div className="text-2xl font-bold text-red-600">ICMS</div>
                        <div className="text-xs text-gray-500 mt-1">Estadual</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-red-200 text-center">
                        <div className="text-2xl font-bold text-red-600">ISS</div>
                        <div className="text-xs text-gray-500 mt-1">Municipal</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-red-200 text-center">
                        <div className="text-2xl font-bold text-red-600">IPI</div>
                        <div className="text-xs text-gray-500 mt-1">Federal</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-red-200 text-center col-span-2">
                        <div className="text-2xl font-bold text-red-600">PIS</div>
                        <div className="text-xs text-gray-500 mt-1">Federal</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-red-200 text-center col-span-2">
                        <div className="text-2xl font-bold text-red-600">COFINS</div>
                        <div className="text-xs text-gray-500 mt-1">Federal</div>
                      </div>
                    </div>
                  </div>

                  {/* Problemas */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                        <i className="ri-error-warning-line text-red-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">27 Legislações de ICMS</h5>
                        <p className="text-sm text-gray-600">Cada estado com regras próprias</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-red-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">Cumulatividade</h5>
                        <p className="text-sm text-gray-600">Imposto sobre imposto em cascata</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                        <i className="ri-file-damage-line text-red-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">+4.000 Páginas de Normas</h5>
                        <p className="text-sm text-gray-600">Complexidade extrema</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                        <i className="ri-time-line text-red-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">1.500 Horas/Ano</h5>
                        <p className="text-sm text-gray-600">Tempo gasto com obrigações fiscais</p>
                      </div>
                    </div>
                  </div>

                  {/* Indicador Visual */}
                  <div className="mt-8 bg-red-100 rounded-xl p-4 text-center">
                    <p className="text-sm font-medium text-red-800">Sistema Complexo e Ineficiente</p>
                  </div>
                </div>
              </div>

              {/* DEPOIS */}
              <div className="relative">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border-2 border-emerald-200 shadow-xl h-full">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-500 rounded-2xl">
                      <i className="ri-checkbox-circle-line text-white text-3xl"></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-emerald-900">DEPOIS</h3>
                      <p className="text-emerald-600 font-medium">Novo Sistema (2033)</p>
                    </div>
                  </div>

                  {/* Novos Impostos */}
                  <div className="mb-8">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="ri-stack-line text-emerald-500"></i>
                      IVA Dual Simplificado
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-xl p-6 border-2 border-emerald-300 text-center shadow-md">
                        <div className="text-3xl font-bold text-emerald-600">CBS</div>
                        <div className="text-sm text-gray-600 mt-2">Contribuição sobre Bens e Serviços</div>
                        <div className="text-xs text-emerald-500 mt-1 font-medium">Federal</div>
                      </div>
                      <div className="bg-white rounded-xl p-6 border-2 border-emerald-300 text-center shadow-md">
                        <div className="text-3xl font-bold text-emerald-600">IBS</div>
                        <div className="text-sm text-gray-600 mt-2">Imposto sobre Bens e Serviços</div>
                        <div className="text-xs text-emerald-500 mt-1 font-medium">Estadual + Municipal</div>
                      </div>
                    </div>
                  </div>

                  {/* Benefícios */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-check-double-line text-emerald-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">1 Legislação Nacional</h5>
                        <p className="text-sm text-gray-600">Regras unificadas em todo o país</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-refund-2-line text-emerald-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">Não-Cumulatividade Plena</h5>
                        <p className="text-sm text-gray-600">Crédito integral em toda cadeia</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-file-check-line text-emerald-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">Simplificação Radical</h5>
                        <p className="text-sm text-gray-600">Menos burocracia e obrigações</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 bg-white/70 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                        <i className="ri-eye-line text-emerald-500 text-xl"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">Transparência Total</h5>
                        <p className="text-sm text-gray-600">Imposto visível na nota fiscal</p>
                      </div>
                    </div>
                  </div>

                  {/* Indicador Visual */}
                  <div className="mt-8 bg-emerald-100 rounded-xl p-4 text-center">
                    <p className="text-sm font-medium text-emerald-800">Sistema Moderno e Eficiente</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparativo em Números */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Comparativo em Números
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="flex justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">5</div>
                    <div className="text-xs text-gray-500">Antes</div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 text-xl self-center"></i>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-500">2</div>
                    <div className="text-xs text-gray-500">Depois</div>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-700">Tributos sobre Consumo</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="flex justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">27</div>
                    <div className="text-xs text-gray-500">Antes</div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 text-xl self-center"></i>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-500">1</div>
                    <div className="text-xs text-gray-500">Depois</div>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-700">Legislações de ICMS/IBS</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="flex justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">1.500h</div>
                    <div className="text-xs text-gray-500">Antes</div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 text-xl self-center"></i>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-500">~500h</div>
                    <div className="text-xs text-gray-500">Depois</div>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-700">Horas em Obrigações/Ano</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                <div className="flex justify-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">Parcial</div>
                    <div className="text-xs text-gray-500">Antes</div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 text-xl self-center"></i>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-500">Total</div>
                    <div className="text-xs text-gray-500">Depois</div>
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-700">Crédito Tributário</p>
              </div>
            </div>
          </div>

          {/* Fluxo Visual da Cadeia Produtiva */}
          <div className="mt-16 bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Fluxo de Créditos na Cadeia Produtiva
            </h3>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Antes - Cumulativo */}
              <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                <h4 className="text-lg font-bold text-red-800 mb-6 flex items-center gap-2">
                  <i className="ri-close-circle-fill text-red-500"></i>
                  Sistema Atual (Cumulativo)
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-building-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Indústria</div>
                      </div>
                    </div>
                    <div className="flex-1 h-2 bg-red-300 rounded relative">
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-red-600">+10% imposto</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-store-2-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Atacado</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-store-2-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Atacado</div>
                      </div>
                    </div>
                    <div className="flex-1 h-2 bg-red-400 rounded relative">
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-red-600">+10% imposto</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-shopping-bag-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Varejo</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-shopping-bag-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Varejo</div>
                      </div>
                    </div>
                    <div className="flex-1 h-2 bg-red-500 rounded relative">
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-red-600">+10% imposto</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-red-200">
                      <div className="text-center">
                        <i className="ri-user-line text-red-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Consumidor</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 bg-red-100 rounded-xl p-4 text-center">
                  <p className="text-sm text-red-800"><strong>Resultado:</strong> Imposto acumulado ~33%</p>
                </div>
              </div>

              {/* Depois - Não Cumulativo */}
              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                <h4 className="text-lg font-bold text-emerald-800 mb-6 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                  Novo Sistema (Não-Cumulativo)
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-building-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Indústria</div>
                      </div>
                    </div>
                    <div className="flex-1 relative">
                      <div className="h-2 bg-emerald-400 rounded"></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-emerald-600">Paga 26,5%</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-store-2-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Atacado</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-store-2-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Atacado</div>
                      </div>
                    </div>
                    <div className="flex-1 relative">
                      <div className="h-2 bg-emerald-400 rounded"></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-emerald-600">Credita e paga diferença</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-shopping-bag-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Varejo</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-shopping-bag-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Varejo</div>
                      </div>
                    </div>
                    <div className="flex-1 relative">
                      <div className="h-2 bg-emerald-400 rounded"></div>
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-medium text-emerald-600">Credita e paga diferença</div>
                    </div>
                    <div className="w-24 h-16 bg-white rounded-lg flex items-center justify-center border border-emerald-200">
                      <div className="text-center">
                        <i className="ri-user-line text-emerald-500 text-xl"></i>
                        <div className="text-xs text-gray-600">Consumidor</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 bg-emerald-100 rounded-xl p-4 text-center">
                  <p className="text-sm text-emerald-800"><strong>Resultado:</strong> Imposto final 26,5% (sem acúmulo)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Destaque Final */}
          <div className="mt-12 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <i className="ri-user-star-line text-white text-5xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Como Contadores Devem se Comportar com a Mudança
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-book-open-line text-white text-2xl"></i>
                      <h4 className="text-lg font-semibold text-white">Atualização Constante</h4>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Invista em cursos, workshops e certificações sobre IBS, CBS e Imposto Seletivo. 
                      A legislação será gradual, mas o conhecimento deve ser imediato.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-computer-line text-white text-2xl"></i>
                      <h4 className="text-lg font-semibold text-white">Tecnologia e Automação</h4>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Adote sistemas que já contemplem os novos tributos. A automação será essencial 
                      para calcular créditos e débitos no novo modelo não-cumulativo.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-team-line text-white text-2xl"></i>
                      <h4 className="text-lg font-semibold text-white">Consultoria Estratégica</h4>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Posicione-se como consultor estratégico. Ajude clientes a entender impactos, 
                      planejar transições e aproveitar benefícios do novo sistema.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-calendar-check-line text-white text-2xl"></i>
                      <h4 className="text-lg font-semibold text-white">Planejamento Antecipado</h4>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Prepare seus clientes desde já. Revise estruturas tributárias, identifique 
                      oportunidades de economia e ajuste processos internos.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-line-chart-line text-white text-2xl"></i>
                      <h4 className="text-lg font-semibold text-white">Análise de Impactos</h4>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      Faça simulações setoriais. Cada segmento será afetado de forma diferente - 
                      serviços, indústria e comércio terão desafios específicos.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border-2 border-white/30">
                    <div className="flex items-start gap-3">
                      <i className="ri-lightbulb-flash-line text-yellow-300 text-2xl flex-shrink-0 mt-1"></i>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Oportunidade de Crescimento</h4>
                        <p className="text-white/95 text-sm leading-relaxed">
                          A Reforma Tributária não é apenas um desafio - é uma oportunidade única para 
                          contadores se destacarem como especialistas essenciais. Quem dominar o novo 
                          sistema primeiro terá vantagem competitiva significativa no mercado.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="mt-16 text-center">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-12 max-w-4xl mx-auto">
          <i className="ri-book-open-line text-white text-5xl mb-4"></i>
          <h3 className="text-3xl font-bold text-white mb-4">
            Precisa de Ajuda Especializada?
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Nossa equipe está preparada para auxiliar sua empresa na transição para o novo sistema tributário
          </p>
          <a 
            href="/contato" 
            className="inline-flex items-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-xl font-semibold hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>Fale com um Especialista</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>

      {/* Timeline Section */}
      <section className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Cronograma de Implementação
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Acompanhe o calendário completo da transição tributária de 2026 a 2033
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-teal-500 via-teal-400 to-teal-300"></div>

          {/* Timeline Items */}
          <div className="space-y-16">
            {/* 2026 */}
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="w-5/12 text-right pr-12">
                  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-500">
                    <h3 className="text-2xl font-bold text-teal-600 mb-3">2026</h3>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Início da Transição</h4>
                    <ul className="text-gray-700 space-y-2 text-base">
                      <li className="flex items-start justify-end">
                        <span>Teste nacional do IBS e CBS</span>
                        <i className="ri-checkbox-circle-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Alíquota teste: 0,9% (IBS) + 0,1% (CBS)</span>
                        <i className="ri-checkbox-circle-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Manutenção integral de PIS/COFINS</span>
                        <i className="ri-checkbox-circle-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Preparação de sistemas e processos</span>
                        <i className="ri-checkbox-circle-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-teal-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center z-10">
                  <i className="ri-flag-fill text-white text-xl"></i>
                </div>
                <div className="w-5/12"></div>
              </div>
            </div>

            {/* 2027 */}
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="w-5/12"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-teal-500 rounded-full border-4 border-white shadow-lg flex items-center justify-center z-10">
                  <i className="ri-play-fill text-white text-xl"></i>
                </div>
                <div className="w-5/12 pl-12">
                  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-r-4 border-teal-500">
                    <h3 className="text-2xl font-bold text-teal-600 mb-3">2027</h3>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Início Efetivo</h4>
                    <ul className="text-gray-700 space-y-2 text-base">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-circle-fill text-teal-500 mr-2 mt-1"></i>
                        <span>IBS e CBS entram em vigor oficialmente</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-circle-fill text-teal-500 mr-2 mt-1"></i>
                        <span>Redução gradual de PIS/COFINS inicia</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-circle-fill text-teal-500 mr-2 mt-1"></i>
                        <span>Cobrança simultânea dos tributos</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-circle-fill text-teal-500 mr-2 mt-1"></i>
                        <span>Adaptação de sistemas contábeis</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 2028-2032 */}
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="w-5/12 text-right pr-12">
                  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-400">
                    <h3 className="text-2xl font-bold text-teal-600 mb-3">2028-2032</h3>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Período de Transição</h4>
                    <ul className="text-gray-700 space-y-2 text-base">
                      <li className="flex items-start justify-end">
                        <span>Redução progressiva de PIS/COFINS</span>
                        <i className="ri-time-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Aumento gradual de IBS e CBS</span>
                        <i className="ri-time-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Convivência de sistemas tributários</span>
                        <i className="ri-time-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Ajustes e correções de rota</span>
                        <i className="ri-time-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                      <li className="flex items-start justify-end">
                        <span>Monitoramento de impactos setoriais</span>
                        <i className="ri-time-fill text-teal-500 ml-2 mt-1"></i>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-teal-50 rounded-lg">
                      <p className="text-sm font-semibold text-teal-700">
                        Período crítico de adaptação empresarial
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-teal-400 rounded-full border-4 border-white shadow-lg flex items-center justify-center z-10">
                  <i className="ri-loader-4-line text-white text-xl"></i>
                </div>
                <div className="w-5/12"></div>
              </div>
            </div>

            {/* 2033 */}
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="w-5/12"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full border-4 border-white shadow-xl flex items-center justify-center z-10">
                  <i className="ri-trophy-fill text-white text-xl"></i>
                </div>
                <div className="w-5/12 pl-12">
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-6 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border-r-4 border-teal-700">
                    <h3 className="text-2xl font-bold text-white mb-3">2033</h3>
                    <h4 className="text-xl font-semibold text-white mb-3">Conclusão da Reforma</h4>
                    <ul className="text-white space-y-2 text-base">
                      <li className="flex items-start">
                        <i className="ri-check-double-fill text-teal-100 mr-2 mt-1"></i>
                        <span>Extinção completa de PIS/COFINS</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-double-fill text-teal-100 mr-2 mt-1"></i>
                        <span>IBS e CBS em alíquotas plenas</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-double-fill text-teal-100 mr-2 mt-1"></i>
                        <span>Sistema tributário unificado</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-double-fill text-teal-100 mr-2 mt-1"></i>
                        <span>Simplificação completa da tributação</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-double-fill text-teal-100 mr-2 mt-1"></i>
                        <span>Novo modelo consolidado</span>
                      </li>
                    </ul>
                    <div className="mt-4 p-4 bg-white/20 rounded-lg backdrop-blur-sm">
                      <p className="text-sm font-semibold text-white">
                        ✨ Marco histórico da tributação brasileira
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-16 bg-gradient-to-r from-teal-50 to-cyan-50 p-8 rounded-2xl border border-teal-200">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-information-fill text-white text-2xl"></i>
            </div>
            <div className="ml-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Importante para Empresas
              </h3>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                O período de transição de 7 anos (2026-2033) foi estabelecido para permitir que empresas, 
                contadores e sistemas se adaptem gradualmente ao novo modelo tributário. Durante este período, 
                haverá cobrança simultânea dos tributos antigos e novos, com redução progressiva dos primeiros 
                e aumento dos segundos.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-teal-600 mb-1">2026</div>
                  <div className="text-sm text-gray-600">Fase de Testes</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-teal-600 mb-1">2027-2032</div>
                  <div className="text-sm text-gray-600">Transição Gradual</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-2xl font-bold text-teal-600 mb-1">2033</div>
                  <div className="text-sm text-gray-600">Sistema Completo</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      {categoryPosts.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Artigos sobre Reforma Tributária
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Análises técnicas e atualizações sobre a transformação do sistema tributário brasileiro
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {categoryPosts.map((article) => (
                <BlogCard key={article.id} post={article} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
